import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Inject,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TemplateMasterService } from '../../../template-master.service';
import {
  Media,
  MediaSelectorDialogDataType,
  MediaSource,
} from '../../template-constants';
import { ImageUploadService } from 'src/app/services/ImageUploadService/image-upload.service';
import { DbService } from 'src/app/services/db-service/db.service';

interface Library {
  text: string;
  value: LIBRARY;
}

enum LIBRARY {
  'UNSPLASH' = 'UNSPLASH',
  'SHUTTERSTOCK' = 'SHUTTERSTOCK',
  'UPLOAD' = 'UPLOAD',
  'CUSTOM_LIBRARY' = 'CUSTOM_LIBRARY',
}

@Component({
  selector: 'app-media-selector',
  templateUrl: './media-selector.component.html',
  styleUrls: ['./media-selector.component.scss'],
})
export class MediaSelectorComponent implements OnInit, AfterViewInit {
  public Object = Object;
  constructor(
    private _dialogRef: MatDialogRef<MediaSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: MediaSelectorDialogDataType,
    private templateService: TemplateMasterService,
    private imageUploadService: ImageUploadService,
    private dbService: DbService
  ) { }

  ngOnInit(): void {
    this.businessId = this.dbService.getBusinessId() as string;
    this.searchBasedOnLibrary();
    this.getPhotosFromLibrary();
  }

  ngAfterViewInit(): void {
    this.createIntersectionObserver();
  }

  @ViewChild('MoreLoader') loaderElement: ElementRef | undefined;

  libraries: Library[] = [
    { text: 'Unsplash', value: LIBRARY.UNSPLASH },
    // { text: 'Shutterstock', value: LIBRARY.SHUTTERSTOCK },
    { text: 'Upload', value: LIBRARY.UPLOAD },
    { text: 'Library', value: LIBRARY.CUSTOM_LIBRARY },
  ];

  selectedLibrary: LIBRARY = LIBRARY.UNSPLASH;

  searchText: string = '';
  pageNo: number = 1;
  pageSize: number = 10;

  selectedImages: { [key: string]: Media } = {};

  uploadedImagesList: Media[] = [];
  imagesList: Media[] = [];

  libraryImagesList: { [key: string]: Media } = {};

  totalPages: number = 0;

  showScreenLoader: boolean = false;
  showButtonLoader: boolean = false;

  businessId: string = '';

  close() {
    this._dialogRef.close();
  }

  createIntersectionObserver() {
    const intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].intersectionRatio <= 0) return;
      // load more content;
      if (this.pageNo === this.totalPages) return;

      this.pageNo++;
      this.searchBasedOnLibrary();
    });
    // start observing
    intersectionObserver.observe(this.loaderElement?.nativeElement);
  }

  changeLibrary(library: Library) {
    this.selectedLibrary = library.value;
    this.pageNo = 1;
    this.searchBasedOnLibrary();
  }

  handleSearch = this.templateService.debounce(() =>
    this.searchBasedOnLibrary()
  );

  searchBasedOnLibrary() {
    if (this.selectedLibrary === LIBRARY.UNSPLASH) {
      this.searchUnsplashPhotos();
    } else if (this.selectedLibrary === LIBRARY.UPLOAD) {
      this.imagesList = this.uploadedImagesList;
    } else {
      for (let uniqueId in this.libraryImagesList) {
        if (this.selectedImages.hasOwnProperty(uniqueId)) {
          this.libraryImagesList[uniqueId].selected = true;
        } else {
          this.libraryImagesList[uniqueId].selected = false;
        }
      }
    }
  }

  searchUnsplashPhotos() {
    if (this.pageNo === 1) {
      this.showScreenLoader = true;
      this.imagesList = [];
    }

    this.templateService
      .searchPhotosInUnsplash(
        this.searchText || 'technology',
        this.pageNo,
        this.pageSize
      )
      .subscribe({
        next: (response: any) => {
          response.results.forEach((image: any) => {
            this.imagesList.push({
              businessId: this.businessId,
              uniqueId: image.id,
              blurHash: image.blur_hash,
              assets: [
                {
                  width: image.width,
                  height: image.height,
                  url: image.urls.thumb,
                  originalUrl: image.urls.raw,
                } as any,
              ],
              author: {
                id: image.user.id,
                userName: image.user.username,
                name: image.user.name,
                twitterUserName: image.user.twitter_username,
                portFolioUrl: image.user.portfolio_url,
                instagramUserName: image.user.instagram_username,
              },
              media: MediaSource.UNSPLASH,
              description: image.description,
              altDescription: image.alt_description,
              selected: this.selectedImages.hasOwnProperty(image.id),
            });
          });

          this.totalPages = response.total_pages;
        },
        complete: () => {
          this.showScreenLoader = false;
        },
      });
  }

  getPhotosFromLibrary() {
    if (this.pageNo === 1) {
      this.showScreenLoader = true;
      this.imagesList = [];
    }

    this.templateService
      .getSavedImagesInLibrary(
        this.businessId,
        '',
        this.pageNo - 1,
        this.pageSize
      )
      .subscribe({
        next: (response: any) => {
          response.data.content.forEach((image: Media) => {
            this.libraryImagesList[image.uniqueId] = {
              ...image,
              description: image.description,
              altDescription: image.altDescription,
              selected: false,
            };
          });

          this.totalPages = Math.ceil(
            response.data.totalElements / this.pageSize
          );
        },
      });
  }
 uploadImages(event: any) {
    let files = event.target.files;

    const maximumFileSize = 4 * 1024 * 1024; // Max is 4mb

    // if upload is happening from smm side, then restricting video/pdf upload specifically
      for (let file of files) {
        if (file.type == "application/pdf") {
         this.templateService.openSnackbar(' PDF uploading is not allowed');
          return;
        }
      }

    for (let file of files) {
      // File size is in bytes
      if (maximumFileSize < file.size) {
        this.templateService.openSnackbar('Maximum file size limit is 4MB');
        continue;
      }

      const reader = new FileReader();

      const selectedImage = new Image();
      const image: Media = {
        businessId: this.businessId,
        uniqueId: file.name + '_' + file.size,
        blurHash: null,
        assets: [{
          width: 0,
          height: 0,
          url: '',
        }],
        author: null,
        media: MediaSource.DEVICE,
        description: file.name,
        altDescription: file.name,
        selected: this.selectedImages.hasOwnProperty(file.name),
        file: file,
      };

      reader.onload = (response: any) => {
        image.assets[0].url = response.target.result;
        selectedImage.src = image.assets[0].url;

        this.uploadedImagesList = [image, ...this.uploadedImagesList];
        this.imagesList = [image,...this.imagesList];

        // Automatically select the uploaded image
        this.selectImage(image);
      };

      // Update the File Height and Width.
      selectedImage.onload = function () {
        image.assets[0].height = selectedImage.height;
        image.assets[0].width = selectedImage.width;
      };

      if (this.imagesList.find((img: Media) => img.description === file.name))
        continue;

      reader.readAsDataURL(file);
    }

  }

  removeUploadedPhoto(removedImage: Media) {
    const imageIndex = this.imagesList.findIndex(
      (img: Media) => img.uniqueId === removedImage.uniqueId
    );

    this.imagesList.splice(imageIndex, 1);

    delete this.selectedImages[removedImage.uniqueId];

    this.uploadedImagesList = [...this.imagesList];
  }

  selectImage(selectedImage: Media) {
    if (
      this.dialogData.hasOwnProperty('multiple') &&
      !this.dialogData['multiple']
    ) {
      const previousSelectedImageIndex = this.imagesList.findIndex(
        (img: Media) => img.selected
      );

      if (previousSelectedImageIndex > -1) {
        this.imagesList[previousSelectedImageIndex].selected = false;
      }
    }

    selectedImage.selected = !selectedImage.selected;

    if (selectedImage.selected) {
      this.selectedImages[selectedImage.uniqueId] = selectedImage;
    } else {
      delete this.selectedImages[selectedImage.uniqueId];
    }
  }

  async insertImages() {
    // if (!Object.keys(this.selectedImages).length) return;

    this.showButtonLoader = true;

    const images: { [key: string]: string } = {};

    if (this.uploadedImagesList.length > 0) {
      for (let image of this.uploadedImagesList) {
        const response: any =
          await this.imageUploadService.uploadPageScreenShots(
            image.file,
            'library-media'
          );

        images[image.uniqueId] = response.Location;
      }
    }
    for (let image of this.imagesList) {
      if (images.hasOwnProperty(image.uniqueId)) {
        image.assets[0].url = images[image.uniqueId];
      }
    }
    let selectedImagesList;
    if (this.selectedLibrary === "UPLOAD") {
      selectedImagesList = this.imagesList;
    }
    else {
      selectedImagesList = Object.values(this.selectedImages);
    }
    // 4. For Unsplash or online images, replace thumbnail URL with original raw URL before saving
    for (let image of selectedImagesList) {
      const asset = image.assets?.[0];
      if (asset && (asset as any).originalUrl) {
        asset.url = (asset as any).originalUrl;
      }
    }
    const sendingMediaRequest = {
      // Already saved images should not be added again
      data: selectedImagesList.filter(
        (img: Media) => !this.libraryImagesList.hasOwnProperty(img.uniqueId)
      ),
    };
    if (sendingMediaRequest.data.length > 0) {
      this.templateService
        .saveImagesToBusinessLibrary(sendingMediaRequest)
        .subscribe({
          next: (response: any) => {
            this._dialogRef.close(sendingMediaRequest.data);
            this.templateService.openSnackbar('Images added to library');
          },
          error: () => {
            this.showButtonLoader = false;
            this.templateService.openSnackbar(
              'Something went wrong, please try again'
            );
          },
        });
    } else {
      this._dialogRef.close(selectedImagesList);
    }
  }
}
